import {
  Box,
  Button,
  Input,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useToast,
  HStack,
  Text,
} from "@chakra-ui/react"
import axios from "axios"
import { useEffect, useState } from "react"
import { api } from "config"

export default function SubTopics() {
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [subTopics, setSubTopics] = useState<any[]>([])
  const [topics, setTopics] = useState<any[]>([])
  const [code, setCode] = useState("")
  const [name, setName] = useState("")
  const [topicId, setTopicId] = useState("")
  const [editId, setEditId] = useState<number | null>(null)
  const [searchCode, setSearchCode] = useState("")
  const [searchName, setSearchName] = useState("")
  const [searchTopic, setSearchTopic] = useState("")
  const [sortAsc, setSortAsc] = useState(true)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [total, setTotal] = useState(0)

  const loadData = async () => {
    try {
      const [stRes, countRes, tRes] = await Promise.all([
        axios.get(`${api}/sub-topics`, {
          params: {
            ...(searchCode && { Code_contains: searchCode }),
            ...(searchName && { Name_contains: searchName }),
            ...(searchTopic && { topic: searchTopic }),
            _sort: sortAsc ? "Code:asc" : "Code:desc",
            _start: (page - 1) * pageSize,
            _limit: pageSize,
          },
          withCredentials: true,
        }),
        axios.get(`${api}/sub-topics/count`, {
          params: {
            ...(searchCode && { Code_contains: searchCode }),
            ...(searchName && { Name_contains: searchName }),
            ...(searchTopic && { topic: searchTopic }),
          },
          withCredentials: true,
        }),
        axios.get(`${api}/topics`, { withCredentials: true }),
      ])

      setSubTopics(stRes.data)
      setTotal(countRes.data)
      setTopics(tRes.data)
    } catch (err) {
      console.error(err)
      toast({ title: "Error loading data", status: "error" })
    }
  }

  useEffect(() => {
    loadData()
  }, [page, searchCode, searchName, searchTopic, sortAsc])

  const openCreate = () => {
    setEditId(null)
    setCode("")
    setName("")
    setTopicId("")
    onOpen()
  }

  const openEdit = (st: any) => {
    setEditId(st.id)
    setCode(st.Code)
    setName(st.Name)
    setTopicId(st.topic?.id)
    onOpen()
  }

  const handleSave = async () => {
    const payload = { Code: code, Name: name, topic: topicId }

    if (editId) {
      await axios.put(`${api}/sub-topics/${editId}`, payload, {
        withCredentials: true,
      })
    } else {
      await axios.post(`${api}/sub-topics`, payload, {
        withCredentials: true,
      })
    }

    toast({ title: "Saved", status: "success" })
    onClose()
    loadData()
  }

  const handleDelete = async (id: number) => {
    await axios.delete(`${api}/sub-topics/${id}`, {
      withCredentials: true,
    })
    toast({ title: "SubTopic deleted", status: "success" })
    loadData()
  }

  return (
    <Box p={5} boxShadow="md" bg="white" borderRadius="md">
      <Button colorScheme="blue" mb={4} onClick={openCreate}>
        + Add SubTopic
      </Button>

      <HStack mb={4} spacing={3}>
        <Input
          placeholder="Search Code"
          value={searchCode}
          onChange={(e) => {
            setSearchCode(e.target.value)
            setPage(1)
          }}
        />

        <Input
          placeholder="Search Subtopic Name"
          value={searchName}
          onChange={(e) => {
            setSearchName(e.target.value)
            setPage(1)
          }}
        />

        <Select
          placeholder="Filter by Topic"
          value={searchTopic}
          onChange={(e) => {
            setSearchTopic(e.target.value)
            setPage(1)
          }}
        >
          {topics.map((t) => (
            <option key={t.id} value={t.id}>
              {t.Name}
            </option>
          ))}
        </Select>
      </HStack>

      <Table variant="simple">
        <Thead bg="gray.100">
          <Tr>
            <Th cursor="pointer" onClick={() => setSortAsc(!sortAsc)}>
              Code {sortAsc ? "▲" : "▼"}
            </Th>
            <Th>Subtopic Name</Th>
            <Th>Topic</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {subTopics.length ? (
            subTopics.map((st) => (
              <Tr key={st.id}>
                <Td>{st.Code}</Td>
                <Td>{st.Name}</Td>
                <Td>{st.topic?.Name || "—"}</Td>
                <Td>
                  <HStack>
                    <Button
                      size="sm"
                      colorScheme="yellow"
                      onClick={() => openEdit(st)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      colorScheme="red"
                      onClick={() => handleDelete(st.id)}
                    >
                      Delete
                    </Button>
                  </HStack>
                </Td>
              </Tr>
            ))
          ) : (
            <Tr>
              <Td colSpan={4} textAlign="center" py={10} color="gray.500">
                No SubTopics Yet
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
      <HStack mt={4}>
        <Button size="sm" onClick={() => setPage((p) => Math.max(p - 1, 1))}>
          Prev
        </Button>

        <Text>
          Page {page} / {Math.ceil(total / pageSize)}
        </Text>

        <Button
          size="sm"
          onClick={() => setPage((p) => p + 1)}
          isDisabled={page * pageSize >= total}
        >
          Next
        </Button>
      </HStack>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editId ? "Edit SubTopic" : "Add SubTopic"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" mb={1}>
              Code
            </Text>
            <Input
              mb={3}
              placeholder="Code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />

            <Text fontSize="sm" mb={1}>
              Subtopic Name
            </Text>
            <Input
              mb={3}
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Select
              placeholder="Select Topic"
              value={topicId}
              onChange={(e) => setTopicId(e.target.value)}
            >
              {topics.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.Name}
                </option>
              ))}
            </Select>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSave}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}
